
import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { getTestTitle, getTestDescription } from "@/services/level1/metadata";
import TestCard from "./TestCard";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Box } from "lucide-react";

interface Test {
  id: string;
  title: string;
  description: string;
  icon: string;
}

interface TestSelectorProps {
  puttingTests: Test[];
  chippingTests: Test[];
  wedgingTests: Test[];
  bunkerTests: Test[];
  ironTests: Test[];
  fairwayWoodTests: Test[];
  driverTests: Test[];
  onSelectTest: (testId: string) => void;
}

const TestSelector: React.FC<TestSelectorProps> = ({ 
  puttingTests, 
  chippingTests, 
  wedgingTests,
  bunkerTests,
  ironTests,
  fairwayWoodTests,
  driverTests,
  onSelectTest 
}) => {
  const { t } = useTranslation('training');
  // Fonction pour rendre un test card avec le titre et la description traduits pour level 1
  const renderTestCard = (test: Test) => {
    const translatedTitle = getTestTitle(test.id);
    const translatedDescription = getTestDescription(test.id);
    return (
      <TestCard
        key={test.id}
        id={test.id}
        title={translatedTitle}
        description={translatedDescription}
        icon={test.icon}
        onSelectTest={onSelectTest}
      />
    );
  };
  
  // Fonction pour rendre une section de tests
  const renderTestSection = (title: string, icon: string, description: string, tests: Test[]) => {
    return (
      <Card className="border-2 border-golf-600 mb-8">
        <CardHeader className="bg-golf-50">
          <div className="flex items-center gap-2">
            <Box className="h-6 w-6 text-golf-700" />
            <CardTitle className="text-xl text-golf-800">{title}</CardTitle>
          </div>
          <CardDescription className="text-golf-600">
            {description}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {tests.map(test => renderTestCard(test))}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.2 }}
      className="mb-12"
    >
      {renderTestSection(
        t('level1.sections.putting.title'), 
        "🎯",
        t('level1.sections.putting.description'),
        puttingTests
      )}
      {renderTestSection(
        t('level1.sections.chipping.title'),
        "⛳",
        t('level1.sections.chipping.description'),
        chippingTests
      )}
      {renderTestSection(
        t('level1.sections.wedging.title'),
        "🏌️",
        t('level1.sections.wedging.description'),
        wedgingTests
      )}
      {renderTestSection(
        t('level1.sections.bunker.title'),
        "🏖️",
        t('level1.sections.bunker.description'),
        bunkerTests
      )}
      {renderTestSection(
        t('level1.sections.ironAndWood.title'),
        "🎯",
        t('level1.sections.ironAndWood.description'),
        [...ironTests, ...fairwayWoodTests]
      )}
      {renderTestSection(
        t('level1.sections.driver.title'),
        "🏌️",
        t('level1.sections.driver.description'),
        driverTests
      )}
    </motion.div>
  );
};

export default TestSelector;
